/**
 * /drill/fret-inversions — Triad Inversions (rung 3).
 *
 * Hear which inversion (maj/min × root/1st/2nd) — place it with the
 * inversion's bass note lowest on the string set.
 */

import { useState, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  expandNode,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type Item,
} from "@music-app/learning-system";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats } from "./FretboardExercise.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

// ─── Item pool ────────────────────────────────────────────────────────────────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-inversions"];
const ALL_ITEMS: Item[] = NODE_IDS.flatMap((id) =>
  expandNode(MVP_NODES.find((n) => n.id === id)!),
);

const QUALITIES = ["maj", "min"] as const;
const INVERSIONS = [0, 1, 2] as const;
const STRING_SETS = [0, 1, 2, 3] as const;

const QUALITY_LABELS: Record<string, string> = { maj: "Major", min: "Minor" };
const INVERSION_LABELS: Record<number, string> = {
  0: "Root pos",
  1: "1st inv",
  2: "2nd inv",
};
const STRING_SET_LABELS: Record<number, string> = {
  0: "e–B–G",
  1: "B–G–D",
  2: "G–D–A",
  3: "D–A–E",
};

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const INVERSIONS_STAT_ROWS = [{ nodeId: "fret-triad-inversions", label: "" }] as const;

function itemQuality(item: Item): string {
  return item.params["quality"] as string;
}
function itemInversion(item: Item): number {
  return item.params["inversion"] as number;
}
function itemStringSet(item: Item): number {
  return item.params["stringSet"] as number;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretInversionsExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [selectedQualities, setSelectedQualities] = useState<ReadonlySet<string>>(
    () => new Set(QUALITIES),
  );
  const [selectedInversions, setSelectedInversions] = useState<ReadonlySet<number>>(
    () => new Set(INVERSIONS),
  );
  const [selectedSets, setSelectedSets] = useState<ReadonlySet<number>>(
    () => new Set(STRING_SETS),
  );

  const toggleQuality = (q: string) =>
    setSelectedQualities((prev) => {
      const next = new Set(prev);
      if (next.has(q)) {
        if (next.size <= 1) return prev;
        next.delete(q);
      } else {
        next.add(q);
      }
      return next;
    });

  const toggleInversion = (inv: number) =>
    setSelectedInversions((prev) => {
      const next = new Set(prev);
      if (next.has(inv)) {
        if (next.size <= 1) return prev;
        next.delete(inv);
      } else {
        next.add(inv);
      }
      return next;
    });

  const toggleSet = (s: number) =>
    setSelectedSets((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        if (next.size <= 1) return prev;
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });

  const nodeItems = useMemo(
    () =>
      ALL_ITEMS.filter(
        (i) =>
          selectedQualities.has(itemQuality(i)) &&
          selectedInversions.has(itemInversion(i)) &&
          selectedSets.has(itemStringSet(i)),
      ),
    [selectedQualities, selectedInversions, selectedSets],
  );

  const safeItems = nodeItems.length > 0 ? nodeItems : ALL_ITEMS;

  return (
    <GuitarDrillCore
      skillId="fret-inversions"
      nodeItems={safeItems}
      exercisePath="/guitar/fret-inversions"
      drillTitle="Triad Inversions"
      mode={mode}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          selectedQualities={selectedQualities}
          onToggleQuality={toggleQuality}
          selectedInversions={selectedInversions}
          onToggleInversion={toggleInversion}
          selectedSets={selectedSets}
          onToggleSet={toggleSet}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  mode,
  from,
  selectedQualities,
  onToggleQuality,
  selectedInversions,
  onToggleInversion,
  selectedSets,
  onToggleSet,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  selectedQualities: ReadonlySet<string>;
  onToggleQuality: (q: string) => void;
  selectedInversions: ReadonlySet<number>;
  onToggleInversion: (inv: number) => void;
  selectedSets: ReadonlySet<number>;
  onToggleSet: (s: number) => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  const modeColor = MODE_COLOR[mode];
  const ACTIVE = { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" };
  const INACTIVE = { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" };
  const [showCustomLength, setShowCustomLength] = useState(false);
  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="Triad Inversions"
        subhead="Hear which inversion — place it with the bass note lowest."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-inversions" />
      <ExerciseLessonLink nodeIds={[...NODE_IDS]} />

      <GuitarNodeStats rows={INVERSIONS_STAT_ROWS} />

      {/* Quality selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Qualities
        </p>
        <div className="flex flex-wrap gap-2">
          {QUALITIES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onToggleQuality(q)}
              aria-pressed={selectedQualities.has(q)}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={selectedQualities.has(q) ? ACTIVE : INACTIVE}
            >
              {QUALITY_LABELS[q]}
            </button>
          ))}
        </div>
      </section>

      {/* Inversion selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Inversions
        </p>
        <div className="flex flex-wrap gap-2">
          {INVERSIONS.map((inv) => (
            <button
              key={inv}
              type="button"
              onClick={() => onToggleInversion(inv)}
              aria-pressed={selectedInversions.has(inv)}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={selectedInversions.has(inv) ? ACTIVE : INACTIVE}
            >
              {INVERSION_LABELS[inv]}
            </button>
          ))}
        </div>
      </section>

      {/* String set selector */}
      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          String sets
        </p>
        <div className="flex flex-wrap gap-2">
          {STRING_SETS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onToggleSet(s)}
              aria-pressed={selectedSets.has(s)}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={selectedSets.has(s) ? ACTIVE : INACTIVE}
            >
              {STRING_SET_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
